import { useState, useEffect, useContext } from 'react';
import { Button } from './ui/button';
import { Bell, X } from 'lucide-react';
import NotificationContext from '../context/NotificationContext';

const NotificationPrompt = () => {
  // Use context directly to handle when not in provider
  const context = useContext(NotificationContext);
  const [dismissed, setDismissed] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  const initialized = context?.initialized ?? false;
  const permissionGranted = context?.permissionGranted ?? false;
  const isConfigured = context?.isConfigured ?? false;
  const requestPermission = context?.requestPermission;

  useEffect(() => {
    // If no context or already dismissed/granted, don't show
    if (!context) return;
    
    // Check if user has already dismissed or granted permission
    const wasDismissed = localStorage.getItem('notification_prompt_dismissed');
    
    // Show prompt after 5 seconds if not dismissed and not already granted
    if (!wasDismissed && !permissionGranted && initialized && isConfigured) {
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [context, initialized, permissionGranted, isConfigured]);

  const handleEnable = async () => {
    if (requestPermission) {
      const granted = await requestPermission();
      if (granted) {
        setShowBanner(false);
      }
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowBanner(false);
    localStorage.setItem('notification_prompt_dismissed', 'true');
  };

  // Don't show if no context, not configured, already granted, or dismissed
  if (!context || !showBanner || dismissed || permissionGranted || !isConfigured) {
    return null;
  }

  return (
    <div className="fixed bottom-24 left-4 right-4 z-50 animate-in slide-in-from-bottom-4">
      <div className="glass rounded-xl p-4 border border-[#FF2E63]/30 shadow-lg shadow-[#FF2E63]/10">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#FF2E63]/20 flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-[#FF2E63]" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-white mb-1">Never miss a tap!</h3>
            <p className="text-sm text-[#A1A1AA] mb-3">
              Get notified when someone taps you or sends a message, even when the app is closed.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={handleEnable}
                size="sm"
                className="bg-[#FF2E63] hover:bg-[#E01E50] text-white"
              >
                Enable notifications
              </Button>
              <Button
                onClick={handleDismiss}
                size="sm"
                variant="ghost"
                className="text-[#A1A1AA] hover:text-white"
              >
                Not now
              </Button>
            </div>
          </div>
          <Button
            onClick={handleDismiss}
            size="icon"
            variant="ghost"
            className="text-[#52525B] hover:text-white -mt-1 -mr-1"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotificationPrompt;
